import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import * as authApi from "@/api/auth";
import type { User, RegisterRequest, SendCodeRequest } from "@/types";

const TOKEN_KEY = "portal_token";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<unknown>;
  sendCode: (req: SendCodeRequest) => Promise<void>;
  register: (req: RegisterRequest) => Promise<unknown>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 应用启动时尝试恢复登录状态
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    authApi
      .getMe()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // 监听 401 事件，清除登录状态
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    };
    window.addEventListener("auth:unauthorized", handler);
    return () => window.removeEventListener("auth:unauthorized", handler);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    try {
      const res = await authApi.login({ username, password });
      localStorage.setItem(TOKEN_KEY, res.token);
      setUser(res.user);
      return res;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendCode = useCallback(async (req: SendCodeRequest) => {
    setLoading(true);
    try {
      await authApi.sendCode(req);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (req: RegisterRequest) => {
    setLoading(true);
    try {
      return await authApi.register(req);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, sendCode, register, logout }),
    [user, loading, login, sendCode, register, logout],
  );

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
